import { useState } from "react";
import { Button } from "@/components/ui/button";
import { TRY } from "@/lib/format";
import { SearchCheck, ChevronDown, ChevronRight, Undo2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Section } from "./StepContact";

const signed = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${TRY(Math.abs(n))}`;
const RESTORE = { removed: "items", premoved: "pays" };

function Part({ p, onRestore }) {
  const [open, setOpen] = useState(true);
  const restore = RESTORE[p.key] && onRestore ? () => onRestore(RESTORE[p.key]) : null;
  return (
    <li className="rounded-md border bg-white px-2.5 py-1.5" data-testid={`import-diff-part-${p.key}`}>
      <div className="flex items-start gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 min-w-0 text-left text-[11px] flex items-start gap-1 disabled:cursor-default" disabled={!p.rows.length}>
          {p.rows.length ? (open ? <ChevronDown className="h-3 w-3 mt-0.5 shrink-0" /> : <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />) : <span className="w-3 shrink-0" />}
          <span>{p.text}{p.rows.length ? <b> ({p.rows.length} satır)</b> : null}</span>
        </button>
        {restore && <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={restore} data-testid={`import-diff-restore-${p.key}`}><Undo2 className="h-3 w-3 mr-1" />Geri al</Button>}
        <b className={cn("tabular-nums text-[11px] shrink-0", p.amount > 0 ? "text-red-700" : "text-emerald-700")} data-testid={`import-diff-part-${p.key}-amount`}>{signed(p.amount)}</b>
      </div>
      {open && p.rows.length > 0 && (
        <ul className="ml-4 mt-1 space-y-px">
          {p.rows.slice(0, 15).map((r, i) => (
            <li key={i} className="font-mono text-[10.5px] text-slate-600 flex gap-2">
              <span className="flex-1 min-w-0 truncate" title={`${r.label}${r.detail ? ` — ${r.detail}` : ""}`}>{r.excelRow ? `Satır ${r.excelRow}: ` : ""}<b className="font-sans text-foreground">{r.label}</b>{r.detail ? <span className="font-sans"> — {r.detail}</span> : null}</span>
              <span className="tabular-nums shrink-0">{signed(r.amount)}</span>
            </li>
          ))}
          {p.rows.length > 15 && <li className="text-[10.5px] italic">… ve {p.rows.length - 15} satır daha</li>}
        </ul>
      )}
    </li>
  );
}

function Block({ title, d, testid, onRestore }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50/50 p-2.5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{title}</span>
        <span className="font-head font-black tabular-nums text-sm text-red-700" data-testid={`${testid}-amount`}>Fark {signed(d.diff)}</span>
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">hesaplanan {TRY(d.calc)} − Excel {TRY(d.excel)} · farkın kaynağı:</div>
      {d.parts.length > 0 && <ul className="mt-1.5 space-y-1">{d.parts.map((p) => <Part key={p.key} p={p} onRestore={onRestore} />)}</ul>}
      {Math.abs(d.unexplained) >= 0.05 && <div className="mt-1.5 text-[11px] text-red-800 font-semibold" data-testid={`${testid}-unexplained`}>Açıklanamayan fark: {signed(d.unexplained)} — Excel dosyasını kontrol edin</div>}
    </div>
  );
}

/** Excel özeti ile hesaplanan toplamlar arasındaki farkın kaynağını satır satır gösterir. */
export default function DiffAnalysis({ diff, onRestore }) {
  const all = [["Toplam Tutar", diff.total, "import-diff-total"], ["Ödemeler", diff.paid, "import-diff-paid"], ["Kalan Bakiye", diff.balance, "import-diff-balance"]].filter(([, d]) => d);
  if (!all.length) return null;
  const bad = all.filter(([, d]) => Math.abs(d.diff) >= 0.05);
  return (
    <Section icon={SearchCheck} title="Fark Analizi" hint="farkın kaynağı" testid="import-sec-diff">
      {bad.length === 0 ? (
        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="import-diff-ok"><CheckCircle2 className="h-4 w-4" />Excel özeti ile hesaplanan toplamlar birebir uyuşuyor — fark yok.</div>
      ) : (
        <div className="space-y-2">{bad.map(([t, d, id]) => <Block key={id} title={t} d={d} testid={id} onRestore={onRestore} />)}</div>
      )}
    </Section>
  );
}
